import { Alert, Box, CircularProgress } from "@mui/material";
import { useEffect } from "react";
import { Outlet, useLocation, useNavigate, useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "features/store";
import { api } from "features/api";
import {
  clearClientView,
  setClientView,
} from "features/clientView/clientViewSlice";
import { useGetAdminUserQuery } from "features/adminUsers/adminUsers";
import ClientViewHeader from "./ClientViewHeader";
import { theme } from "../hoc/Theme";
import { ThemeProvider } from "@mui/material/styles";
import CustomScrollbars from "../CustomScrollbars";
import Footer from "../Footer";
import { ClientViewProvider } from "contexts/ClientViewContext";
import { SHELL_VIEWPORT_HEIGHT_SX } from "config";

/** Pages that manage their own scrolling and must not sit inside CustomScrollbars. */
const FULL_BLEED_SEGMENTS = [
  "/data-storage",
  "/user-management",
  "/sftp-management",
  "/bots-management",
  "/partner-management",
  "/integrations-beta",
];

export default function ClientViewLayout() {
  const { userId } = useParams<{ userId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const fileSystemFullBleed = FULL_BLEED_SEGMENTS.some((segment) =>
    location.pathname.includes(segment),
  );
  const dispatch = useAppDispatch();
  const clientView = useAppSelector((state) => state.clientView);

  const parsedId = userId ? Number(userId) : NaN;
  const targetId = Number.isNaN(parsedId) ? null : parsedId;

  const {
    data: viewedUser,
    isLoading,
    isError,
  } = useGetAdminUserQuery(targetId as number, { skip: targetId === null });
  // Viewing as someone inherits their data scope; the card says whether this
  // viewer may, and the API refuses the header otherwise.
  const forbidden = viewedUser?.client_view?.allowed === false;

  useEffect(() => {
    if (targetId === null) {
      navigate("/user-management", { replace: true });
    }
  }, [targetId, navigate]);

  useEffect(() => {
    if (targetId === null || !viewedUser || forbidden) return;
    if (clientView.active && clientView.userId === targetId) return;
    const displayName =
      [viewedUser.first_name, viewedUser.last_name]
        .filter(Boolean)
        .join(" ")
        .trim() || viewedUser.username;
    dispatch(
      setClientView({
        userId: targetId,
        displayName,
        profile: {
          ...viewedUser.profile,
          user_type: viewedUser.user_type,
          owned_client_ids: viewedUser.owned_client_ids,
        },
      }),
    );
  }, [
    targetId,
    viewedUser,
    forbidden,
    clientView.active,
    clientView.userId,
    dispatch,
  ]);

  // Leaving the shell any other way than handleBack - browser Back, a direct
  // URL, a logout - must clear the slice too, or the app keeps sending
  // X-View-As-User and refusing writes while showing normal-mode chrome.
  useEffect(
    () => () => {
      dispatch(clearClientView());
      dispatch(api.util.resetApiState());
    },
    [dispatch],
  );

  const handleBack = () => {
    dispatch(clearClientView());
    dispatch(api.util.resetApiState());
    navigate("/user-management");
  };

  if (targetId === null) return null;

  if (isError || forbidden) {
    return (
      <ThemeProvider theme={theme}>
        <Box sx={{ p: 3 }}>
          <Alert severity="error">
            {forbidden
              ? (viewedUser?.client_view?.reason ??
                "You cannot view the platform as this user.")
              : "This user could not be opened in Client View. They may have been archived or removed."}
          </Alert>
        </Box>
      </ThemeProvider>
    );
  }

  if (!clientView.active || clientView.userId !== targetId || isLoading) {
    return (
      <ThemeProvider theme={theme}>
        <Box
          sx={{
            ...SHELL_VIEWPORT_HEIGHT_SX,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: "background.default",
          }}
        >
          <CircularProgress />
        </Box>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <Box
        sx={{
          ...SHELL_VIEWPORT_HEIGHT_SX,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          bgcolor: "background.default",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            flex: "1 1 0",
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          <ClientViewHeader onBackToUserManagement={handleBack}>
            {fileSystemFullBleed ? (
              <Box
                component="main"
                sx={{
                  flex: "1 1 0",
                  minHeight: 0,
                  display: "flex",
                  flexDirection: "column",
                  overflow: "hidden",
                }}
              >
                <ClientViewProvider value={true}>
                  <Outlet />
                </ClientViewProvider>
              </Box>
            ) : (
              <Box
                component="main"
                sx={{
                  flex: 1,
                  minHeight: 0,
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <CustomScrollbars sx={{ flex: 1, minHeight: 0 }}>
                  <ClientViewProvider value={true}>
                    <Outlet />
                  </ClientViewProvider>
                </CustomScrollbars>
              </Box>
            )}
          </ClientViewHeader>
        </Box>

        <Footer />
      </Box>
    </ThemeProvider>
  );
}
